import { useEffect, useId, useRef, useState } from 'react'
import { PersonName } from '@/components/PersonName'
import { createPortal } from 'react-dom'
import { api, ApiError } from '@/api/client'
import { Icon } from '@/components/Icon'
import { fmtDateTime } from '@/lib/format'
import type { Role, TaskDetail } from '@/lib/types'

export function RetirementNotice({ data }: { data: TaskDetail }) {
  const retirement = data.task.retirement
  return <section className="retirement-notice" aria-label="Retired task">
    <div className="retirement-notice-icon"><Icon name="archive" size={22} /></div>
    <div><h2>Task retired</h2><p className="muted">This task has left the pipeline. Its materials and history are available below.</p>
      {retirement && <p className="small muted">Retired by <PersonName people={data.people} id={retirement.actor.user_id} email={retirement.actor.email} /> · {fmtDateTime(retirement.at)}</p>}
      {retirement?.feedback && <p className="prewrap retirement-feedback">{retirement.feedback}</p>}
    </div>
  </section>
}

export function TaskLifecycle({ data, role, dirty, onSaved }: {
  data: TaskDetail; role: Role; dirty: boolean; onSaved: (data: TaskDetail) => void
}) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [feedback, setFeedback] = useState('')
  const button = useRef<HTMLButtonElement>(null)
  const { task, lifecycle_permissions: permissions } = data
  if (role !== 'engineer') return null
  const retired = task.lifecycle === 'retired'
  const awaitingResume = !retired && task.stage === 3 && (task.authoring?.cancel_reason === 'task_retired' || !task.authoring && task.retirement?.stage === 3)
  if (!permissions?.can_retire && !permissions?.can_restore && !awaitingResume) return error ? <p className="note err" role="alert">{error}</p> : null
  async function submit(action: 'retire' | 'restore' | 'resume-build') {
    if (busy || dirty) return
    setBusy(true); setError('')
    try {
      const updated = await api.post<TaskDetail>(`/eng/tasks/${task.task_id}/${action}`, {
        revision: task.revision ?? 0, ...(action === 'retire' ? { feedback: feedback.trim() } : {}),
      })
      onSaved(updated)
      setConfirming(false); setFeedback('')
    } catch (e) {
      setError((e as Error).message)
      if (e instanceof ApiError && e.status === 409) {
        try { onSaved(await api.get<TaskDetail>(`/tasks/${task.task_id}`)) }
        catch { /* Keep the dialog and feedback usable while disconnected. */ }
      }
    }
    finally { setBusy(false) }
  }
  return <>
    {awaitingResume && <section className="task-lifecycle" aria-label="Resume restored build">
      <div><h3>Continue the restored build</h3>
        <p className="muted small">The saved inputs and latest checkpoint will be used for a fresh authoring job.</p>
        {!permissions?.can_resume_build && <p className="small muted" role="status">Cleaning up the previous worker before the build can resume…</p>}
      </div>
      <button className="btn" disabled={busy || dirty || !permissions?.can_resume_build} onClick={() => void submit('resume-build')}>
        <Icon name="play" size={17} />{busy ? 'Resuming…' : 'Resume build'}
      </button>
    </section>}
    <section className="task-lifecycle" aria-label="Task lifecycle">
    <div><h3>{retired ? 'Return to the pipeline' : 'Retire this task'}</h3>
      <p className="muted small">{retired ? 'Restore this task to its previous stage. Reviews start fresh; builds resume when you choose.'
        : 'Set this task aside if it should not continue. You can restore it later.'}</p>
      {dirty && <p className="small muted">Save or discard your changes before retiring.</p>}
    </div>
    <button ref={button} className={retired ? 'btn' : 'retire-task-button'}
      disabled={busy || dirty}
      onClick={() => { setError(''); if (retired) void submit('restore'); else setConfirming(true) }}>
      <Icon name={retired ? 'play' : 'archive'} size={17} />
      {busy ? retired ? 'Restoring…' : 'Retiring…' : retired ? 'Restore task' : 'Retire task'}
    </button>
    {!confirming && error && <p className="note err" role="alert">{error}</p>}
    {confirming && <RetirementDialog busy={busy} error={error} feedback={feedback} setFeedback={setFeedback}
      returnFocus={button.current} onCancel={() => { setConfirming(false); setError('') }} onConfirm={() => submit('retire')} />}
  </section></>
}

function RetirementDialog({ busy, error, feedback, setFeedback, returnFocus, onCancel, onConfirm }: {
  busy: boolean; error: string; feedback: string; setFeedback: (value: string) => void
  returnFocus: HTMLButtonElement | null; onCancel: () => void; onConfirm: () => Promise<void>
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const id = useId()
  useEffect(() => {
    const element = dialog.current!
    element.showModal()
    element.querySelector<HTMLButtonElement>('button')?.focus()
    return () => { element.close(); returnFocus?.focus() }
  }, [returnFocus])
  return createPortal(<dialog ref={dialog} className="review-confirmation retirement-dialog"
    aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
    onCancel={event => { event.preventDefault(); if (!busy) onCancel() }}>
    <span className="retirement-dialog-icon"><Icon name="archive" size={24} /></span>
    <h2 id={`${id}-title`}>Retire this task?</h2>
    <p id={`${id}-description`}>The task will leave the pipeline and its current reviews and builds will stop.
      Its materials and history will be kept. A developer can restore it later.</p>
    <label htmlFor={`${id}-feedback`}>Feedback <span className="muted">(optional)</span></label>
    <textarea id={`${id}-feedback`} rows={4} maxLength={4000} disabled={busy} value={feedback}
      onChange={event => setFeedback(event.target.value)} placeholder="Why should this task be set aside?" />
    <p className="muted small">Your feedback will be visible to the task’s expert.</p>
    {error && <p className="note err" role="alert">{error}</p>}
    <div className="row retirement-dialog-actions">
      <button autoFocus disabled={busy} onClick={onCancel}>Cancel</button>
      <button className="retire-task-button" disabled={busy} onClick={() => void onConfirm()}><Icon name="archive" size={17} />{busy ? 'Retiring…' : 'Retire task'}</button>
    </div>
  </dialog>, document.body)
}
